import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const sections = {
  en: [
    {
      title: '1. Controller',
      content: 'The controller responsible for data processing on this website is:\n\nStup-On\nEmail: hello@stup-on.de\n\nBy using this website, you agree to the collection and use of information in accordance with this policy.'
    },
    {
      title: '2. Data We Collect',
      content: 'We collect the following personal data:\n\n• Account data: email address and password (hashed) when you register\n• Contact form data: name, email, subject, and message\n• Usage data: pages visited, browser type, IP address (anonymised), timestamps\n• Cookie data: session identifiers and preference settings'
    },
    {
      title: '3. Legal Basis for Processing (GDPR Art. 6)',
      content: 'We process your data on the following legal bases:\n\n• Art. 6(1)(a) – Consent: for optional cookies and marketing communications\n• Art. 6(1)(b) – Contract performance: to provide the service you signed up for\n• Art. 6(1)(c) – Legal obligation: for invoicing and tax records\n• Art. 6(1)(f) – Legitimate interest: for security, fraud prevention, and analytics'
    },
    {
      title: '4. How We Use Your Data',
      content: 'Your data is used to:\n\n• Create and manage your user account\n• Provide access to the 10-step methodology and tools\n• Process payments via Stripe (payment data is processed by Stripe, not stored by us)\n• Respond to contact form enquiries\n• Send transactional emails (account confirmation, password reset)\n• Improve platform performance and security'
    },
    {
      title: '5. Data Sharing',
      content: 'We do not sell your personal data. We share data only with:\n\n• Supabase (database & authentication hosting) – EU-based servers\n• Stripe (payment processing) – PCI-DSS compliant\n• Vercel / hosting provider – for website delivery\n\nAll processors are bound by data processing agreements in accordance with GDPR Art. 28.'
    },
    {
      title: '6. Data Retention',
      content: 'We retain your data for as long as your account is active. Upon account deletion:\n\n• Account data is deleted within 30 days\n• Contact form submissions are retained for 2 years for legal purposes\n• Payment records are retained for 10 years per German tax law (§ 147 AO)'
    },
    {
      title: '7. Your Rights (GDPR Art. 15–22)',
      content: 'You have the right to:\n\n• Access – request a copy of your personal data\n• Rectification – correct inaccurate data\n• Erasure – request deletion ("right to be forgotten")\n• Restriction – limit how we process your data\n• Portability – receive your data in a machine-readable format\n• Objection – object to processing based on legitimate interest\n• Withdraw consent – at any time, without affecting prior processing\n\nTo exercise any right, contact: hello@stup-on.de'
    },
    {
      title: '8. Cookies',
      content: 'We use strictly necessary cookies for session management and, with your consent, analytics cookies. You can manage your preferences at any time via our Cookie Settings page. For full details, see our Cookie Policy.'
    },
    {
      title: '9. Security',
      content: 'We implement appropriate technical and organisational measures to protect your data, including TLS encryption, hashed passwords, and role-based access controls. However, no internet transmission is 100% secure.'
    },
    {
      title: '10. Supervisory Authority',
      content: 'You have the right to lodge a complaint with a data protection supervisory authority. In Germany, the relevant authority is:\n\nDer Bundesbeauftragte für den Datenschutz und die Informationsfreiheit (BfDI)\nwww.bfdi.bund.de'
    },
    {
      title: '11. Changes to This Policy',
      content: 'We may update this Privacy Policy from time to time. We will notify you of significant changes by email or a prominent notice on our website. The date of the last update is shown below.'
    }
  ],
  de: [
    {
      title: '1. Verantwortlicher',
      content: 'Verantwortlicher für die Datenverarbeitung auf dieser Website ist:\n\nStup-On\nE-Mail: hello@stup-on.de\n\nMit der Nutzung dieser Website stimmst du der Erhebung und Verwendung von Informationen gemäß dieser Richtlinie zu.'
    },
    {
      title: '2. Erhobene Daten',
      content: 'Wir erheben folgende personenbezogene Daten:\n\n• Kontodaten: E-Mail-Adresse und Passwort (gehasht) bei der Registrierung\n• Kontaktformulardaten: Name, E-Mail, Betreff und Nachricht\n• Nutzungsdaten: besuchte Seiten, Browsertyp, IP-Adresse (anonymisiert), Zeitstempel\n• Cookie-Daten: Sitzungskennungen und Einstellungen'
    },
    {
      title: '3. Rechtsgrundlage der Verarbeitung (Art. 6 DSGVO)',
      content: 'Wir verarbeiten deine Daten auf folgenden Rechtsgrundlagen:\n\n• Art. 6 Abs. 1 lit. a – Einwilligung: für optionale Cookies und Marketing\n• Art. 6 Abs. 1 lit. b – Vertragserfüllung: zur Erbringung des gebuchten Dienstes\n• Art. 6 Abs. 1 lit. c – Rechtliche Verpflichtung: für Rechnungs- und Steuerunterlagen\n• Art. 6 Abs. 1 lit. f – Berechtigtes Interesse: für Sicherheit, Betrugsprävention und Analysen'
    },
    {
      title: '4. Verwendung deiner Daten',
      content: 'Deine Daten werden verwendet, um:\n\n• Dein Benutzerkonto zu erstellen und zu verwalten\n• Zugang zur 10-Schritte-Methodik und den Tools bereitzustellen\n• Zahlungen über Stripe zu verarbeiten (Zahlungsdaten werden von Stripe verarbeitet, nicht von uns gespeichert)\n• Anfragen über das Kontaktformular zu beantworten\n• Transaktions-E-Mails zu versenden (Kontobestätigung, Passwortreset)\n• Die Plattform zu verbessern und zu sichern'
    },
    {
      title: '5. Datenweitergabe',
      content: 'Wir verkaufen deine Daten nicht. Wir geben Daten nur weiter an:\n\n• Supabase (Datenbank & Authentifizierungs-Hosting) – Server in der EU\n• Stripe (Zahlungsabwicklung) – PCI-DSS-konform\n• Vercel / Hosting-Anbieter – zur Website-Bereitstellung\n\nAlle Auftragsverarbeiter sind durch Verträge gemäß Art. 28 DSGVO gebunden.'
    },
    {
      title: '6. Speicherdauer',
      content: 'Wir speichern deine Daten, solange dein Konto aktiv ist. Nach der Kontolöschung:\n\n• Kontodaten werden innerhalb von 30 Tagen gelöscht\n• Kontaktformular-Einsendungen werden 2 Jahre aufbewahrt\n• Zahlungsbelege werden 10 Jahre gemäß deutschem Steuerrecht (§ 147 AO) aufbewahrt'
    },
    {
      title: '7. Deine Rechte (Art. 15–22 DSGVO)',
      content: 'Du hast das Recht auf:\n\n• Auskunft – Kopie deiner personenbezogenen Daten anfordern\n• Berichtigung – unrichtige Daten korrigieren lassen\n• Löschung – Löschung deiner Daten verlangen ("Recht auf Vergessenwerden")\n• Einschränkung – Verarbeitung einschränken lassen\n• Datenübertragbarkeit – Daten in maschinenlesbarem Format erhalten\n• Widerspruch – Verarbeitung aufgrund berechtigter Interessen widersprechen\n• Widerruf der Einwilligung – jederzeit, ohne Auswirkung auf die bisherige Verarbeitung\n\nKontakt: hello@stup-on.de'
    },
    {
      title: '8. Cookies',
      content: 'Wir verwenden technisch notwendige Cookies für die Sitzungsverwaltung und mit deiner Einwilligung Analyse-Cookies. Du kannst deine Präferenzen jederzeit über unsere Cookie-Einstellungen verwalten.'
    },
    {
      title: '9. Sicherheit',
      content: 'Wir setzen geeignete technische und organisatorische Maßnahmen ein, um deine Daten zu schützen, darunter TLS-Verschlüsselung, gehashte Passwörter und rollenbasierte Zugriffskontrollen.'
    },
    {
      title: '10. Aufsichtsbehörde',
      content: 'Du hast das Recht, eine Beschwerde bei einer Datenschutz-Aufsichtsbehörde einzureichen. In Deutschland ist dies:\n\nDer Bundesbeauftragte für den Datenschutz und die Informationsfreiheit (BfDI)\nwww.bfdi.bund.de'
    },
    {
      title: '11. Änderungen dieser Richtlinie',
      content: 'Wir können diese Datenschutzerklärung von Zeit zu Zeit aktualisieren. Über wesentliche Änderungen informieren wir dich per E-Mail oder durch einen deutlichen Hinweis auf unserer Website.'
    }
  ]
};

const PrivacyPolicy = () => {
  const { lang } = useLanguage();
  const isDE = lang === 'de';

  return (
    <div className="bg-white py-16 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="mb-10">
            <span className="inline-block bg-blue-50 text-blue-600 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">GDPR / DSGVO</span>
            <h1 className="text-4xl font-black text-gray-900 mb-3">
              {isDE ? 'Datenschutzerklärung' : 'Privacy Policy'}
            </h1>
            <p className="text-gray-500 text-sm">
              {isDE ? 'Zuletzt aktualisiert: Januar 2025' : 'Last updated: January 2025'}
            </p>
          </div>

          <div className="space-y-8">
            {sections[lang].map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="border-b border-gray-100 pb-8 last:border-0"
              >
                <h2 className="text-xl font-bold text-gray-900 mb-3">{section.title}</h2>
                <p className="text-gray-600 leading-relaxed whitespace-pre-line">{section.content}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;